import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ResolvedQuizSponsorsStylePreset } from '@/lib/domain/sponsorsStylePreset';
import { resolveQuizSponsorsStylePreset } from '@/lib/services/sponsorsStylePresets';

export type QuizSponsorsStylePresetSessionStatus =
  'unresolved' | 'loading' | 'refreshing' | 'ready' | 'failed';

type ResolutionLoader = (quizId: string) => Promise<ResolvedQuizSponsorsStylePreset>;

interface UseQuizSponsorsStylePresetSessionOptions {
  quizId: string | null | undefined;
  /** Public Playback bootstrap has already frozen this revision for the run. */
  initialResolution?: ResolvedQuizSponsorsStylePreset | null;
  load?: ResolutionLoader;
}

export interface QuizSponsorsStylePresetSession {
  status: QuizSponsorsStylePresetSessionStatus;
  resolution: ResolvedQuizSponsorsStylePreset | null;
  error: Error | null;
  refresh(): void;
  retry(): void;
}

export function useQuizSponsorsStylePresetSession({
  quizId,
  initialResolution = null,
  load = resolveQuizSponsorsStylePreset,
}: UseQuizSponsorsStylePresetSessionOptions): QuizSponsorsStylePresetSession {
  const normalizedQuizId = quizId?.trim() || null;
  const requestSequenceRef = useRef(0);
  const cacheRef = useRef(new Map<string, ResolvedQuizSponsorsStylePreset>());
  const [requestEpoch, setRequestEpoch] = useState(0);
  const [state, setState] = useState<{
    status: QuizSponsorsStylePresetSessionStatus;
    resolution: ResolvedQuizSponsorsStylePreset | null;
    error: Error | null;
    resolvedQuizId: string | null;
  }>(() => ({
    status:
      normalizedQuizId && initialResolution?.quizId === normalizedQuizId
        ? 'ready'
        : normalizedQuizId
          ? 'loading'
          : 'unresolved',
    resolution:
      normalizedQuizId && initialResolution?.quizId === normalizedQuizId ? initialResolution : null,
    error: null,
    resolvedQuizId:
      normalizedQuizId && initialResolution?.quizId === normalizedQuizId ? normalizedQuizId : null,
  }));

  useEffect(() => {
    if (!normalizedQuizId) {
      requestSequenceRef.current += 1;
      setState({
        status: 'unresolved',
        resolution: null,
        error: null,
        resolvedQuizId: null,
      });
      return;
    }

    if (requestEpoch === 0 && initialResolution?.quizId === normalizedQuizId) {
      cacheRef.current.set(normalizedQuizId, initialResolution);
      setState({
        status: 'ready',
        resolution: initialResolution,
        error: null,
        resolvedQuizId: normalizedQuizId,
      });
      return;
    }

    const sequence = ++requestSequenceRef.current;
    const cachedResolution = cacheRef.current.get(normalizedQuizId) ?? null;
    setState((current) => {
      const sameQuiz = current.resolvedQuizId === normalizedQuizId;
      const resolution = cachedResolution ?? (sameQuiz ? current.resolution : null);
      return {
        status: resolution ? 'refreshing' : 'loading',
        resolution,
        error: null,
        resolvedQuizId: resolution ? normalizedQuizId : null,
      };
    });

    void load(normalizedQuizId).then(
      (resolution) => {
        if (requestSequenceRef.current !== sequence) return;
        if (resolution.quizId !== normalizedQuizId) {
          setState({
            status: 'failed',
            resolution: null,
            error: new Error('Resolved Sponsors & Style preset belongs to another Quiz'),
            resolvedQuizId: normalizedQuizId,
          });
          return;
        }
        cacheRef.current.set(normalizedQuizId, resolution);
        setState({
          status: 'ready',
          resolution,
          error: null,
          resolvedQuizId: normalizedQuizId,
        });
      },
      (error: unknown) => {
        if (requestSequenceRef.current !== sequence) return;
        setState((current) => {
          const sameQuiz = current.resolvedQuizId === normalizedQuizId;
          return {
            status: 'failed',
            resolution: sameQuiz ? current.resolution : null,
            error:
              error instanceof Error
                ? error
                : new Error('The Quiz Sponsors & Style preset could not be loaded.'),
            resolvedQuizId: normalizedQuizId,
          };
        });
      }
    );

    return () => {
      if (requestSequenceRef.current === sequence) {
        requestSequenceRef.current += 1;
      }
    };
  }, [initialResolution, load, normalizedQuizId, requestEpoch]);

  const refresh = useCallback(() => setRequestEpoch((current) => current + 1), []);
  const visibleResolution = state.resolvedQuizId === normalizedQuizId ? state.resolution : null;
  const visibleStatus =
    normalizedQuizId === null
      ? 'unresolved'
      : state.resolvedQuizId === normalizedQuizId || state.status === 'loading'
        ? state.status
        : 'loading';

  return useMemo(
    () => ({
      status: visibleStatus,
      resolution: visibleResolution,
      error: state.error,
      refresh,
      retry: refresh,
    }),
    [refresh, state.error, visibleResolution, visibleStatus]
  );
}
